import React, { useContext, useState, useEffect, useRef } from 'react'
import { flushSync } from 'react-dom'
import { FirebaseContext } from '../../Firebase'
import { useLocation } from '@reach/router'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBars, faChevronDown } from '@fortawesome/free-solid-svg-icons'
import {
  faHome,
  faCompass,
  faFeather,
  faShapes,
  faBullseyeArrow,
  faTimeline,
  faFileUser,
  faReply,
  faMapMarked,
} from '@fortawesome/pro-light-svg-icons'
import {
  faSearch,
  faGlobe,
} from '@fortawesome/pro-regular-svg-icons'
import {
  faColumns,
  faMapMarked as faMapMarkedSolid,
  faHome as faHomeSolid,
  faCompass as faCompassSolid,
  faFeather as faFeatherSolid,
  faShapes as faShapesSolid,
  faBullseyeArrow as faBullseyeArrowSolid,
  faFileUser as faFileUserSolid,
} from '@fortawesome/pro-solid-svg-icons'
import ProfileImage from '../../profile-image/profile-image'
import Link, { navigate, changeLocale } from '../../../link'
import { getCurrentPageLanguage } from '../../../linkUtils'
import { languageNames } from '../../../i18n'
import GlobalSearch from '../../search/GlobalSearch'
import OutsideClickHandler from 'react-outside-click-handler'
import NotificationsDropdown from '../../dashboard/notifications/notifications-dropdown'
import LEVELS from '../../levels/Levels'
import LevelsModal from '../../levels/LevelsModal'
import GetStartedFlyout from '../../get-started/get-started'
import { calculateCompletionPercent } from '../../get-started/utils/utils'
import ProgressCircle from '../../progress-circle/progress-circle'
import { FormattedMessage, useIntl } from 'react-intl'
import { educatorRoles, Role } from '../../educator/utils'
import WingsLogo from '../../../../static/assets/images/wingspans-w-icon_black.svg'
import WingsLogoFull from '../../../../static/assets/images/Wingspans-new-logo.svg'
import { tabsAccessibleToProspectiveStudents } from '../../dashboard/prospective-student-tool/tabsAccessibleToProspectiveStudents'
import useWingsAppNavKeyboardNavigation from '../../hooks/useWingsAppNavKeyboardNavigation'
import './WingsAppNav.css'

// Must match CSS variables in WingsAppNav.css :root
// --wings-app-nav-sidebar-collapsed-width, --wings-app-nav-sidebar-expanded-width, --wings-app-nav-top-bar-height
const SIDEBAR_COLLAPSED_WIDTH = '72px'
const SIDEBAR_EXPANDED_WIDTH = '17rem'
const SIDEBAR_MINIMIZED_WIDTH = '0rem'
const TOP_BAR_HEIGHT = '3.5rem'

const MINIMIZED = 'MINIMIZED'
const COLLAPSED = 'COLLAPSED'
const MAXIMIZED = 'MAXIMIZED'

const isTouchLayoutDevice = () => {
  if (typeof window === 'undefined') return false
  return window.matchMedia('(hover: none)').matches || window.matchMedia('(pointer: coarse)').matches
}

const NAV_ITEMS = [
  {
    icon: faHome,
    solidIcon: faHomeSolid,
    to: '/dashboard',
    labelId: 'wings-app-nav-dashboard',
    defaultLabel: 'Home',
    visibleToProspectiveStudents: true,
  },
  {
    icon: faFeather,
    solidIcon: faFeatherSolid,
    to: '/dashboard?tab=assessment',
    labelId: 'wings-app-nav-assessment',
    defaultLabel: 'Assessments',
    visibleToProspectiveStudents: true,
  },
  {
    icon: faCompass,
    solidIcon: faCompassSolid,
    to: '/dashboard?tab=explore',
    labelId: 'wings-app-nav-explore',
    defaultLabel: 'Exploration',
    visibleToProspectiveStudents: false,
  },
  {
    icon: faShapes,
    solidIcon: faShapesSolid,
    to: '/dashboard?tab=work-readiness',
    labelId: 'wings-app-nav-work-readiness',
    defaultLabel: 'Skills Development',
    visibleToProspectiveStudents: false,
  },
  {
    icon: faFileUser,
    solidIcon: faFileUserSolid,
    to: '/dashboard?tab=portfolio',
    labelId: 'wings-app-nav-portfolio',
    defaultLabel: 'Resume Builder',
    visibleToProspectiveStudents: false,
  },
  {
    icon: faMapMarked,
    solidIcon: faMapMarkedSolid,
    to: '/dashboard?tab=career-plan',
    labelId: 'wings-app-nav-career-plans',
    defaultLabel: 'Career Plans',
    visibleToProspectiveStudents: false,
  },
  {
    icon: faBullseyeArrow,
    solidIcon: faBullseyeArrowSolid,
    to: '/dashboard?tab=goal-tracking',
    labelId: 'wings-app-nav-goal-tracking',
    defaultLabel: 'Goal Tracking',
    visibleToProspectiveStudents: false,
  },
]

const EXPLORE_SUB_ITEMS = [
  { to: '/dashboard?tab=explore&category=People', labelId: 'algolia-toggle-dropdown-people', defaultLabel: 'People', category: 'People' },
  { to: '/dashboard?tab=explore&category=Careers', labelId: 'algolia-toggle-dropdown-careers', defaultLabel: 'Careers', category: 'Careers' },
  { to: '/dashboard?tab=explore&category=Schools', labelId: 'algolia-toggle-dropdown-schools', defaultLabel: 'Schools', category: 'Schools' },
  { to: '/dashboard?tab=explore&category=Majors', labelId: 'algolia-toggle-dropdown-majors', defaultLabel: 'Majors', category: 'Majors' },
  // {
  //   to: '/dashboard?tab=explore&category=Companies',
  //   labelId: 'algolia-toggle-dropdown-companies',
  //   defaultLabel: 'Companies',
  //   category: 'Companies',
  // },
  // { to: '/dashboard?tab=explore&category=Videos', labelId: 'algolia-toggle-dropdown-videos', defaultLabel: 'Videos', category: 'Videos' },
]

const HIDDEN_PATHS = [
  '/login',
  '/login/',
  '/login/individuals',
  '/login/individuals/',
  '/signup/individuals',
  '/signup/individuals/',
  '/signup/tokens',
  '/signup/tokens/',
  '/es-ES/login',
  '/es-ES/login/',
  '/es-ES/login/individuals',
  '/es-ES/login/individuals/',
  '/es-ES/signup/individuals',
  '/es-ES/signup/individuals/',
  '/es-ES/signup/tokens',
  '/es-ES/signup/tokens/',
  '/passwordless/authentication',
  '/passwordless/authentication/',
  '/educator/',
  '/educator',
  '/educator/students',
  '/educator/students/',
  '/profile/',
  '/certificates/',
  '/certificates',
]

/**
 * WingsAppNav — Hybrid navigation component for authenticated users.
 * Renders a collapsible left sidebar with icon-based navigation and a
 * fixed top-right search control.
 * @returns {JSX.Element|null}
 */
const WingsAppNav = () => {
  const { formatMessage } = useIntl()
  const {
    firebase,
    user,
    profile,
    localFirestore: { level, pointTotalRecord },
    walkThroughStepValues,
    setWalkThroughStepValues,
    prospectivePreviewCode,
    setProspectivePreviewCode,
  } = useContext(FirebaseContext)

  // MINIMIZED = hidden (0), COLLAPSED = icons only (4.5rem), MAXIMIZED = full labels (15rem)
  const [sidebarState, setSidebarState] = useState(COLLAPSED)
  const [animate, setAnimate] = useState(false)
  const preHoverState = useRef(COLLAPSED)
  const searchContainerRef = useRef(null)
  const searchFocusRetryTimeoutRef = useRef(null)
  const sidebarRef = useRef(null)
  const topBarRef = useRef(null)
  const sidebarToggleRef = useRef(null)
  const searchToggleRef = useRef(null)
  const topProfileButtonRef = useRef(null)
  const sidebarProfileButtonRef = useRef(null)
  const [showSearch, setShowSearch] = useState(false)
  const [showLanguageMenu, setShowLanguageMenu] = useState(false)
  const [showFlyout, setShowFlyout] = useState(false)
  const [showLevelModal, setShowLevelModal] = useState(false)
  const [showProfileMenu, setShowProfileMenu] = useState(false)
  const [getStartedProgress, setGetStartedProgress] = useState(-1)
  const [walkthroughStepsLoaded, setWalkthroughStepsLoaded] = useState(false)
  const [isVisible, setIsVisible] = useState(true)
  const [showExploreSubMenu, setShowExploreSubMenu] = useState(false)
  const shouldFocusContentAfterKeyboardNavRef = useRef(false)

  const isLoggedIn = !!user // Only render for authenticated users
  const isProspectiveStudent = prospectivePreviewCode || (!!profile && profile.prospectiveSchoolCode)
  const isEducatorOrAdmin = !!profile && (educatorRoles.includes(profile.role) || profile?.membershipType === Role.EDUCATOR)
  const location = useLocation()
  const queryParams = new URLSearchParams(location?.search || '')
  const currentTab = queryParams.get('tab')
  const currentExploreCategory = queryParams.get('category')
  const onDashboard = (location?.pathname ?? '').startsWith('/dashboard') || (location?.pathname ?? '').startsWith('/es-ES/dashboard')
  const isDashboardPage = onDashboard && !isProspectiveStudent
  const isExploreTabActive = currentTab === 'explore'

  const currentLocale = getCurrentPageLanguage(location?.pathname ?? '')
  const getLanguageOptionLabel = (languageKey) =>
    languageKey === 'es-ES'
      ? formatMessage({ id: 'wings-app-nav-language-spanish', defaultMessage: 'Spanish' })
      : formatMessage({ id: 'wings-app-nav-language-english', defaultMessage: 'English' })
  const currentLanguageName = getLanguageOptionLabel(currentLocale)

  const handleLocaleChange = async (localeKey) => {
    changeLocale(localeKey)
    setShowLanguageMenu(false)
    try {
      await firebase?.updateProfile({
        data: { language: localeKey === 'es-ES' ? 'esp' : 'eng' },
      })
    } catch (err) {
      console.error('Error updating profile language', err)
    }
  }

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (!showLanguageMenu) return
      if (e.target?.closest?.('.wings-app-nav-language-menu-anchor')) return
      setShowLanguageMenu(false)
    }
    document.addEventListener('mousedown', handleOutsideClick)
    return () => document.removeEventListener('mousedown', handleOutsideClick)
  }, [showLanguageMenu])

  // Show/hide based on current path and auth state
  useEffect(() => {
    const currentPath = location?.pathname ?? ''
    const shouldHide =
      !isLoggedIn ||
      HIDDEN_PATHS.some((path) => currentPath.startsWith(path)) ||
      (isProspectiveStudent && onDashboard && !tabsAccessibleToProspectiveStudents.has(currentTab))
    setIsVisible(!shouldHide)
  }, [location, isLoggedIn, isProspectiveStudent, onDashboard, currentTab])

  useEffect(() => {
    if (isExploreTabActive) {
      setShowExploreSubMenu(true)
    }
  }, [isExploreTabActive])

  // On dashboard: restore MINIMIZED → COLLAPSED. On non-dashboard: always reset to MINIMIZED.
  useEffect(() => {
    setAnimate(false)
    setSidebarState((prev) => {
      if (isDashboardPage) return prev === MINIMIZED ? COLLAPSED : prev
      return MINIMIZED
    })
  }, [isDashboardPage])

  // Close the mobile overlay whenever the route changes (any nav link click navigates).
  // Mobile-only: on desktop the sidebar opens/closes via hover, and forcing it closed here
  // would cause it to shrink-and-expand whenever the user clicks a tab while hovered.
  useEffect(() => {
    if (typeof window === 'undefined') return
    const isTouch = isTouchLayoutDevice()
    if (!isTouch) return
    setSidebarState((prev) => {
      if (prev !== MAXIMIZED) return prev
      return isDashboardPage ? COLLAPSED : MINIMIZED
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.pathname, location?.search])

  // Manage layout offsets via body classes — CSS variables handle the actual values
  useEffect(() => {
    if (typeof document === 'undefined') return

    if (isVisible && isLoggedIn) {
      document.body.classList.add('wings-app-nav-active')
      // Clear any inline paddingTop set by the public header before auth loaded
      document.body.style.paddingTop = ''
    } else {
      document.body.classList.remove('wings-app-nav-active')
      document.body.classList.remove('wings-app-nav-dashboard')
    }

    return () => {
      document.body.classList.remove('wings-app-nav-active')
      document.body.classList.remove('wings-app-nav-dashboard')
    }
  }, [isVisible, isLoggedIn])

  // Apply sidebar offset class on dashboard pages only
  useEffect(() => {
    if (typeof document === 'undefined') return

    if (isVisible && isDashboardPage) {
      document.body.classList.add('wings-app-nav-dashboard')
    } else {
      document.body.classList.remove('wings-app-nav-dashboard')
    }

    return () => {
      document.body.classList.remove('wings-app-nav-dashboard')
    }
  }, [isVisible, isDashboardPage])

  // Fetch get-started walkthrough steps
  useEffect(() => {
    let isCancelled = false

    const fetchWalkthroughSteps = async () => {
      if (!firebase || !user) return
      try {
        const res = await firebase.getWalkthroughSteps()
        if (isCancelled) return
        setWalkthroughStepsLoaded(true)
        if (res) {
          setWalkThroughStepValues((prev) => ({ ...prev, ...res }))
        }
      } catch (err) {
        console.error('Error fetching walkthrough steps:', err)
      }
    }

    fetchWalkthroughSteps()
    return () => {
      isCancelled = true
    }
  }, [firebase, user])

  // Sync get-started progress percentage
  useEffect(() => {
    setGetStartedProgress(calculateCompletionPercent(walkThroughStepValues))
  }, [walkThroughStepValues])

  const handleLogout = async () => {
    try {
      await firebase.updateProfile({ data: { logoutDate: new Date() } })
      await firebase.logout()
    } catch (err) {
      console.error('Error logging out:', err)
    }
    // Full page redirect so all React/Firebase state is torn down cleanly.
    // Client-side navigation (navigate("/")) leaves stale auth context alive
    // long enough for the sidebar to persist until onAuthStateChanged fires.
    window.location.replace('/')
  }

  const handleWalkThroughStepChange = async (stepKey) => {
    const updatedValue = !walkThroughStepValues[stepKey]
    setWalkThroughStepValues((prev) => ({ ...prev, [stepKey]: updatedValue }))
    await firebase.saveWalkthroughStep(stepKey, updatedValue)
  }

  const {
    handleSidebarKeyDown,
    handleTopBarKeyDown,
    getSidebarFocusables,
    sidebarOnFocusCapture,
    topBarOnFocusCapture,
    wasLastInteractionKeyboardRef,
  } = useWingsAppNavKeyboardNavigation({
    sidebarRef,
    topBarRef,
    sidebarToggleRef,
    sidebarState,
  })

  const handleOutsideSearchClick = (e) => {
    const isSearchToggle = e.target?.closest?.('.wings-app-nav-search-toggle')
    if (!isSearchToggle) {
      setShowSearch(false)
    }
  }

  const focusSearchInput = ({ forceKeyboard = false } = {}) => {
    const inputElement = searchContainerRef.current?.querySelector('input')
    if (!inputElement) return

    inputElement.focus({ preventScroll: true })

    if (forceKeyboard) {
      // iOS Safari often requires an additional direct interaction signal
      // to reliably open the software keyboard.
      inputElement.click()
      const inputValueLength = inputElement.value?.length ?? 0
      if (typeof inputElement.setSelectionRange === 'function') {
        inputElement.setSelectionRange(inputValueLength, inputValueLength)
      }
    }
  }

  const queueTouchSearchFocus = () => {
    if (typeof window === 'undefined') return

    requestAnimationFrame(() => {
      focusSearchInput({ forceKeyboard: true })
    })

    if (searchFocusRetryTimeoutRef.current) {
      window.clearTimeout(searchFocusRetryTimeoutRef.current)
    }

    // Retry once after the open animation/frame settles.
    searchFocusRetryTimeoutRef.current = window.setTimeout(() => {
      focusSearchInput({ forceKeyboard: true })
    }, 180)
  }

  const handleSearchToggle = () => {
    const isTouch = isTouchLayoutDevice()

    if (showSearch) {
      if (typeof window !== 'undefined' && searchFocusRetryTimeoutRef.current) {
        window.clearTimeout(searchFocusRetryTimeoutRef.current)
      }
      setShowSearch(false)
      return
    }

    // Mobile: opening the search closes the sidebar overlay
    setSidebarState((s) => {
      if (s !== MAXIMIZED) return s
      setAnimate(true)
      return isDashboardPage ? COLLAPSED : MINIMIZED
    })

    if (isTouch) {
      // iOS Safari requires focus to happen in the same trusted interaction.
      // Commit showSearch synchronously, then focus immediately.
      flushSync(() => {
        setShowSearch(true)
      })
      focusSearchInput({ forceKeyboard: true })
      queueTouchSearchFocus()
      return
    }

    setShowSearch(true)
  }

  useEffect(() => {
    if (showSearch) requestAnimationFrame(focusSearchInput)
  }, [showSearch])

  useEffect(() => {
    return () => {
      if (typeof window !== 'undefined' && searchFocusRetryTimeoutRef.current) {
        window.clearTimeout(searchFocusRetryTimeoutRef.current)
      }
    }
  }, [])

  // Keyboard accessibility: Escape closes whichever overlay/menu is open and
  // restores focus to the control that opened it.
  useEffect(() => {
    if (typeof window === 'undefined') return
    const handleKeyDown = (e) => {
      if (e.key !== 'Escape') return
      if (showSearch) {
        setShowSearch(false)
        searchToggleRef.current?.focus()
        return
      }
      if (showProfileMenu) {
        setShowProfileMenu(false)
        // Restore focus to whichever profile button is currently visible
        const btn = topProfileButtonRef.current?.offsetParent ? topProfileButtonRef.current : sidebarProfileButtonRef.current
        btn?.focus()
        return
      }
      if (showLanguageMenu) {
        setShowLanguageMenu(false)
        return
      }
      if (sidebarState === MAXIMIZED) {
        setAnimate(true)
        setSidebarState(isDashboardPage ? COLLAPSED : MINIMIZED)
        sidebarToggleRef.current?.focus()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [showSearch, showProfileMenu, showLanguageMenu, sidebarState, isDashboardPage])

  /**
   * Determines whether a nav item's route matches the current page.
   * Handles both exact path matches and dashboard tab query params.
   * @param {string} to - The nav item's destination path
   * @returns {boolean}
   */
  const isNavItemActive = (to) => {
    const rawPathname = location?.pathname ?? ''
    // Strip locale prefix so Spanish routes (/es-ES/...) match the same nav items
    const pathname = rawPathname.replace(/^\/es-ES/, '') || '/'
    const search = location?.search ?? ''
    const [basePath, tabParam] = to.split('?')

    if (!tabParam) {
      return (pathname === basePath || pathname === `${basePath}/`) && !search.includes('tab=')
    }

    return pathname.startsWith(basePath) && search.includes(tabParam)
  }

  const effectiveSidebarState = !isDashboardPage && sidebarState !== MAXIMIZED ? MINIMIZED : sidebarState

  const sidebarWidth =
    effectiveSidebarState === MAXIMIZED
      ? SIDEBAR_EXPANDED_WIDTH
      : effectiveSidebarState === MINIMIZED
        ? SIDEBAR_MINIMIZED_WIDTH
        : SIDEBAR_COLLAPSED_WIDTH

  const pointsNumber = Number(pointTotalRecord ?? 0)
  const pointsDigitCount = Number.isFinite(pointsNumber)
    ? Math.abs(Math.trunc(pointsNumber)).toString().length
    : String(pointTotalRecord ?? 0).replace(/\D/g, '').length
  const shouldCompactPoints = pointsDigitCount >= 4

  const getSidebarStateForDestination = (toPath) => {
    const destination = toPath?.split('?')[0] ?? ''
    if (destination.startsWith('/dashboard')) return COLLAPSED
    return MINIMIZED
  }

  const focusMainContentAfterNavigation = () => {
    if (typeof document === 'undefined') return

    const focusTargetSelectors = [
      '#roadmap-action-plan-focus-target',
      '#roadmap-featured-stories-focus-target',
      '.dashboard-tab-panel [data-keyboard-item]',
      '.dashboard-tab-panel [data-keyboard-region]',
      '.dashboard-tab-panel a[href], .dashboard-tab-panel button:not([disabled]), .dashboard-tab-panel input:not([disabled]), .dashboard-tab-panel select:not([disabled]), .dashboard-tab-panel textarea:not([disabled]), .dashboard-tab-panel [tabindex]:not([tabindex="-1"])',
      'main [data-main-content-focus-start="true"]',
      'main a[href], main button:not([disabled]), main input:not([disabled]), main select:not([disabled]), main textarea:not([disabled]), main [tabindex]:not([tabindex="-1"])',
    ]

    const target = focusTargetSelectors
      .map((selector) => document.querySelector(selector))
      .find((element) => element instanceof HTMLElement && element.offsetParent !== null)

    if (target instanceof HTMLElement) {
      target.focus()
      return
    }

    const main = document.querySelector('main')
    if (!(main instanceof HTMLElement)) return

    const hasTabIndex = main.hasAttribute('tabindex')
    if (!hasTabIndex) {
      main.setAttribute('tabindex', '-1')
    }
    main.focus()
  }

  const handleSidebarKeyboardNavigation = (toPath) => {
    shouldFocusContentAfterKeyboardNavRef.current = true
    setAnimate(true)
    setSidebarState(getSidebarStateForDestination(toPath))
  }

  const handleSidebarNavItemKeyDown = (e, toPath) => {
    if (e.key !== 'Enter' && e.key !== ' ') return
    handleSidebarKeyboardNavigation(toPath)
  }

  const handleSidebarNavItemClick = (toPath) => {
    if (!wasLastInteractionKeyboardRef.current) return
    handleSidebarKeyboardNavigation(toPath)
  }

  const isExploreSubItemActive = (category) => isExploreTabActive && currentExploreCategory === category

  useEffect(() => {
    if (!shouldFocusContentAfterKeyboardNavRef.current) return

    shouldFocusContentAfterKeyboardNavRef.current = false
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        focusMainContentAfterNavigation()
      })
    })
  }, [location?.pathname, location?.search])

  if (!isVisible || !isLoggedIn) return null

  return (
    <>
      {/* ─── Mobile backdrop overlay ────────────────────────────── */}
      {(sidebarState === MAXIMIZED || showSearch) && (
        <div
          className="wings-app-nav-mobile-backdrop"
          onClick={() => {
            if (showSearch) {
              setShowSearch(false)
            } else {
              setShowProfileMenu(false)
              setAnimate(true)
              setSidebarState(isDashboardPage ? COLLAPSED : MINIMIZED)
            }
          }}
          aria-hidden="true"
        />
      )}

      {/* ─── Left Sidebar ──────────────────────────────────────── */}
      <OutsideClickHandler
        onOutsideClick={(e) => {
          if (e.target?.closest?.('.wings-app-nav-sidebar-toggle')) return
          setSidebarState((prev) => {
            if (prev !== MAXIMIZED) return prev
            return isDashboardPage ? COLLAPSED : MINIMIZED
          })
        }}
      >
        <nav
          className={`wings-app-nav-sidebar printer-none wings-app-nav-sidebar-${sidebarState}${animate ? ' wings-app-nav-sidebar-animated' : ''}`}
          style={{ width: sidebarWidth, overflow: effectiveSidebarState === MINIMIZED ? 'hidden' : undefined }}
          role="navigation"
          aria-label={formatMessage({ id: 'wings-app-nav-main-navigation', defaultMessage: 'Main navigation' })}
          id="wings-app-nav-sidebar"
          aria-hidden={effectiveSidebarState === MINIMIZED ? 'true' : undefined}
          inert={effectiveSidebarState === MINIMIZED ? 'true' : undefined}
          ref={sidebarRef}
          onKeyDown={handleSidebarKeyDown}
          onFocusCapture={sidebarOnFocusCapture}
          onMouseEnter={() => {
            if (!isDashboardPage) return
            if (isTouchLayoutDevice()) return
            preHoverState.current = sidebarState
            setAnimate(true)
            setSidebarState(MAXIMIZED)
          }}
          onMouseLeave={() => {
            if (!isDashboardPage) return
            if (isTouchLayoutDevice()) return
            setAnimate(true)
            setSidebarState(preHoverState.current)
          }}
          onFocus={(e) => {
            // When entering via keyboard (Tab / Shift+Tab) from outside the sidebar,
            // redirect focus to the first item so navigation always starts at the top.
            const comingFromOutside = !sidebarRef.current?.contains(e.relatedTarget)
            if (comingFromOutside && wasLastInteractionKeyboardRef.current) {
              const focusables = getSidebarFocusables()
              if (focusables.length && focusables[0] !== e.target) {
                focusables[0].focus()
                return
              }
            }
            // Keyboard equivalent of hover-to-expand on desktop dashboard.
            // Guard: relatedTarget is null when the browser restores focus after
            // switching back to this tab — don't expand in that case.
            if (!e.relatedTarget) return
            if (!isDashboardPage) return
            if (isTouchLayoutDevice()) return
            if (sidebarState === MAXIMIZED) return
            preHoverState.current = sidebarState
            setAnimate(true)
            setSidebarState(MAXIMIZED)
          }}
          onBlur={(e) => {
            // Collapse only when focus leaves the sidebar entirely.
            if (!isDashboardPage) return
            if (isTouchLayoutDevice()) return
            // relatedTarget is null when clicking a non-focusable area (e.g. empty
            // space inside the sidebar). Don't collapse — the OutsideClickHandler
            // handles clicks that genuinely land outside.
            if (!e.relatedTarget) return
            if (e.currentTarget.contains(e.relatedTarget)) return
            setAnimate(true)
            setSidebarState(preHoverState.current)
          }}
        >
          {/* Navigation Items */}
          <ul className="wings-app-nav-items">
            {NAV_ITEMS.filter((item) => !isProspectiveStudent || item.visibleToProspectiveStudents).map(
              ({ icon, solidIcon, to, labelId, defaultLabel }) => (
                <li key={to} className={to === '/dashboard?tab=explore' ? 'wings-app-nav-group-item' : ''}>
                  {to === '/dashboard?tab=explore' ? (
                    <>
                      <button
                        type="button"
                        className={`wings-app-nav-item-link wings-app-nav-item-button ${isExploreTabActive ? 'wings-app-nav-item-active' : ''}`}
                        title={
                          sidebarState !== MAXIMIZED
                            ? formatMessage({ id: labelId, defaultMessage: defaultLabel })
                            : undefined
                        }
                        onClick={() => setShowExploreSubMenu((prev) => !prev)}
                        aria-expanded={showExploreSubMenu}
                        aria-controls="wings-app-nav-explore-sub-menu"
                      >
                        <FontAwesomeIcon icon={isExploreTabActive ? solidIcon : icon} className="wings-app-nav-item-icon" />
                        <span className="wings-app-nav-item-label">
                          <FormattedMessage id={labelId} defaultMessage={defaultLabel} />
                        </span>
                        <FontAwesomeIcon
                          icon={faChevronDown}
                          className={`wings-app-nav-item-chevron${showExploreSubMenu ? ' wings-app-nav-item-chevron-open' : ''}`}
                        />
                      </button>

                      <ul
                        id="wings-app-nav-explore-sub-menu"
                        className={`wings-app-nav-sub-items${showExploreSubMenu ? ' wings-app-nav-sub-items-open' : ''}`}
                        aria-hidden={!showExploreSubMenu}
                      >
                        {EXPLORE_SUB_ITEMS.map((subItem) => (
                          <li key={subItem.to}>
                            <Link
                              to={subItem.to}
                              className={`wings-app-nav-sub-item-link ${isExploreSubItemActive(subItem.category) ? 'wings-app-nav-sub-item-active' : ''}`}
                              onKeyDown={(e) => handleSidebarNavItemKeyDown(e, subItem.to)}
                              onClick={() => handleSidebarNavItemClick(subItem.to)}
                              tabIndex={showExploreSubMenu ? 0 : -1}
                            >
                              <span className="wings-app-nav-item-label wings-app-nav-sub-item-label">
                                <FormattedMessage id={subItem.labelId} defaultMessage={subItem.defaultLabel} />
                              </span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </>
                  ) : (
                    <Link
                      to={to}
                      className={`wings-app-nav-item-link ${isNavItemActive(to) ? 'wings-app-nav-item-active' : ''}`}
                      title={
                        sidebarState !== MAXIMIZED
                          ? formatMessage({ id: labelId, defaultMessage: defaultLabel })
                          : undefined
                      }
                      onKeyDown={(e) => handleSidebarNavItemKeyDown(e, to)}
                      onClick={() => handleSidebarNavItemClick(to)}
                    >
                      <FontAwesomeIcon icon={isNavItemActive(to) ? solidIcon : icon} className="wings-app-nav-item-icon" />
                      <span className="wings-app-nav-item-label">
                        <FormattedMessage id={labelId} defaultMessage={defaultLabel} />
                      </span>
                    </Link>
                  )}
                </li>
              )
            )}
          </ul>

          {/* Bottom Icon Row */}
          <div
            className="wings-app-nav-sidebar-bottom"
            inert={effectiveSidebarState === MINIMIZED ? 'true' : undefined}
          >
            {!isProspectiveStudent && level !== null && (
              <button
                className="wings-app-nav-sidebar-level-btn"
                onClick={() => setShowLevelModal(true)}
                title={LEVELS[level]?.name ?? formatMessage({ id: 'wings-app-nav-view-level', defaultMessage: 'View Level' })}
              >
                <span className="wings-app-nav-bottom-icon-wrap wings-app-nav-points-icon-wrap">
                  <span className={`wings-app-nav-points-icon${shouldCompactPoints ? ' wings-app-nav-points-icon-compact' : ''}`}>{pointTotalRecord ?? 0}</span>
                </span>
                <span className="wings-app-nav-item-label">
                  <FormattedMessage id="wings-app-nav-points" defaultMessage="Points" />
                </span>
              </button>
            )}
            <div className="wings-app-nav-sidebar-language-mobile wings-app-nav-language-menu-anchor">
              <button
                type="button"
                className="wings-app-nav-language-toggle"
                onClick={() => setShowLanguageMenu((prev) => !prev)}
                aria-label={formatMessage({ id: 'wings-app-nav-language-menu', defaultMessage: 'Language menu' })}
                aria-haspopup="menu"
                aria-expanded={showLanguageMenu}
              >
                <span className="wings-app-nav-bottom-icon-wrap">
                  <FontAwesomeIcon icon={faGlobe} className="wings-app-nav-item-icon" />
                </span>
                <span className="wings-app-nav-item-label wings-app-nav-language-toggle-label">{currentLanguageName}</span>
                <FontAwesomeIcon icon={faChevronDown} className={`wings-app-nav-language-toggle-swap${showLanguageMenu ? ' active' : ''}`} />
              </button>
              {showLanguageMenu && (
                <div className="wings-app-nav-sidebar-language-menu" role="menu">
                  {Object.keys(languageNames).map((languageKey) => (
                    <button
                      key={languageKey}
                      type="button"
                      role="menuitemradio"
                      aria-checked={currentLocale === languageKey}
                      className={`wings-app-nav-sidebar-language-menu-item${currentLocale === languageKey ? ' active' : ''}`}
                      onClick={() => handleLocaleChange(languageKey)}
                    >
                      {getLanguageOptionLabel(languageKey)}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {isEducatorOrAdmin && (
              <Link
                to="/educator/"
                className={`wings-app-nav-item-link wings-app-nav-admin-link ${isNavItemActive('/educator') ? 'wings-app-nav-item-active' : ''}`}
                title={
                  sidebarState !== MAXIMIZED
                    ? formatMessage({ id: 'wings-app-nav-admin', defaultMessage: 'Admin Dashboard' })
                    : undefined
                }
                onClick={() => setProspectivePreviewCode("")}
              >
                <span className="wings-app-nav-bottom-icon-wrap">
                  <FontAwesomeIcon icon={faReply} className="wings-app-nav-item-icon" />
                </span>
                <span className="wings-app-nav-item-label">
                  <FormattedMessage id="wings-app-nav-admin" defaultMessage="Admin Dashboard" />
                </span>
              </Link>
            )}
            <div className="wings-app-nav-sidebar-bottom-row">
              <div className="wings-app-nav-profile-wrapper wings-app-nav-sidebar-profile-wrapper">
                <button
                  className="wings-app-nav-sidebar-profile-btn"
                  ref={sidebarProfileButtonRef}
                  onClick={() => setShowProfileMenu((prev) => !prev)}
                  aria-label={formatMessage({ id: 'wings-app-nav-account-menu', defaultMessage: 'Account menu' })}
                  aria-haspopup="menu"
                  aria-expanded={showProfileMenu}
                >
                  <ProfileImage profile={profile} className="wings-app-nav-profile-image" />
                </button>
                {showProfileMenu && (
                  <div className="wings-app-nav-profile-menu wings-app-nav-profile-menu-upward" role="menu">
                    <Link to="/dashboard" className="wings-app-nav-profile-menu-item" role="menuitem" onClick={() => setShowProfileMenu(false)}>
                      <FormattedMessage id="account-menu-my-dashboard" defaultMessage="My Dashboard" />
                    </Link>
                    <Link to="/member/account" className="wings-app-nav-profile-menu-item" role="menuitem" onClick={() => setShowProfileMenu(false)}>
                      <FormattedMessage id="account-menu-my-account" defaultMessage="My Account" />
                    </Link>
                    <button className="wings-app-nav-profile-menu-item" role="menuitem" onClick={handleLogout}>
                      <FormattedMessage id="account-menu-logout" defaultMessage="Logout" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </nav>
      </OutsideClickHandler>

      {/* ─── Top Bar ───────────────────────────────────────────── */}
      <div
        className="wings-app-nav-top-bar printer-none"
        ref={topBarRef}
        onKeyDown={handleTopBarKeyDown}
        onFocusCapture={topBarOnFocusCapture}
      >
        {/* Sidebar toggle */}
        <div className="wings-app-nav-top-bar-left">
          <button
            className="wings-app-nav-search-toggle wings-app-nav-sidebar-toggle"
            ref={sidebarToggleRef}
            onClick={() => {
              setAnimate(true)
              setSidebarState((prev) => {
                const next = isDashboardPage ? (prev === MAXIMIZED ? COLLAPSED : MAXIMIZED) : prev === MAXIMIZED ? MINIMIZED : MAXIMIZED
                // Mobile: opening the sidebar closes the search panel
                if (next === MAXIMIZED) setShowSearch(false)
                return next
              })
            }}
            aria-label={
              sidebarState === MAXIMIZED
                ? formatMessage({ id: 'wings-app-nav-collapse-sidebar', defaultMessage: 'Collapse sidebar' })
                : formatMessage({ id: 'wings-app-nav-expand-sidebar', defaultMessage: 'Expand sidebar' })
            }
            aria-expanded={sidebarState === MAXIMIZED}
            aria-controls="wings-app-nav-sidebar"
          >
            <FontAwesomeIcon icon={faColumns} className="wings-app-nav-toggle-icon-desktop" />
            <FontAwesomeIcon icon={faBars} className={`wings-app-nav-toggle-icon-mobile${sidebarState === MAXIMIZED ? ' active' : ''}`} />
          </button>
          <Link to="/dashboard" className="wings-app-nav-top-bar-logo-link">
            <img src={WingsLogoFull} alt="Wingspans" className="wings-app-nav-top-bar-logo-full" />
          </Link>
        </div>

        {/* Right controls */}
        <div className="wings-app-nav-top-bar-right">
          {!isProspectiveStudent && (
            <>
              <div className="wings-app-nav-search-wrapper">
                <OutsideClickHandler onOutsideClick={handleOutsideSearchClick}>
                  <div
                    ref={searchContainerRef}
                    id="wings-app-nav-search-container"
                    className={`wings-app-nav-search-container ${showSearch ? 'show' : ''}`}
                    aria-hidden={!showSearch}
                  >
                    <div className="wings-app-nav-search-inner">
                      <GlobalSearch onSelect={() => setShowSearch(false)} disableEnterSelect={true} />
                    </div>
                  </div>
                </OutsideClickHandler>
                <button
                  className={`wings-app-nav-search-toggle${showSearch ? ' active' : ''}`}
                  ref={searchToggleRef}
                  onClick={handleSearchToggle}
                  aria-label={formatMessage({ id: 'wings-app-nav-toggle-search', defaultMessage: 'Toggle search' })}
                  aria-expanded={showSearch}
                  aria-controls="wings-app-nav-search-container"
                >
                  <FontAwesomeIcon icon={faSearch} />
                </button>
              </div>

              <LevelsModal userOpened={showLevelModal} handleShowLevelModalHide={() => setShowLevelModal(false)} />

              <div className="wings-app-nav-top-language wings-app-nav-top-language-desktop wings-app-nav-language-menu-anchor">
                <button
                  type="button"
                  className="wings-app-nav-top-language-toggle"
                  onClick={() => setShowLanguageMenu((prev) => !prev)}
                  aria-label={formatMessage({ id: 'wings-app-nav-language-menu', defaultMessage: 'Language menu' })}
                  aria-haspopup="menu"
                  aria-expanded={showLanguageMenu}
                >
                  <FontAwesomeIcon icon={faGlobe} className="wings-app-nav-top-language-icon" />
                </button>

                {showLanguageMenu && (
                  <div className="wings-app-nav-top-language-menu" role="menu">
                    {Object.keys(languageNames).map((languageKey) => (
                      <button
                        key={languageKey}
                        type="button"
                        role="menuitemradio"
                        aria-checked={currentLocale === languageKey}
                        className={`wings-app-nav-top-language-menu-item${currentLocale === languageKey ? ' active' : ''}`}
                        onClick={() => handleLocaleChange(languageKey)}
                      >
                        {getLanguageOptionLabel(languageKey)}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <NotificationsDropdown profile={profile} />
            </>
          )}

          <div className="wings-app-nav-top-bar-profile-outer">
            <OutsideClickHandler
              onOutsideClick={() => {
                if (isTouchLayoutDevice()) return
                setShowProfileMenu(false)
              }}
            >
              <div className="wings-app-nav-profile-wrapper wings-app-nav-top-bar-profile">
                <button
                  className="wings-app-nav-profile-button"
                  ref={topProfileButtonRef}
                  onClick={() => setShowProfileMenu((prev) => !prev)}
                  aria-label={formatMessage({ id: 'wings-app-nav-account-menu', defaultMessage: 'Account menu' })}
                  aria-haspopup="menu"
                  aria-expanded={showProfileMenu}
                >
                  <ProfileImage profile={profile} className="wings-app-nav-profile-image" />
                </button>

                {showProfileMenu && (
                  <div className="wings-app-nav-profile-menu" role="menu">
                    <Link to="/dashboard" className="wings-app-nav-profile-menu-item" role="menuitem" onClick={() => setShowProfileMenu(false)}>
                      <FormattedMessage id="account-menu-my-dashboard" defaultMessage="My Dashboard" />
                    </Link>
                    <Link to="/member/account" className="wings-app-nav-profile-menu-item" role="menuitem" onClick={() => setShowProfileMenu(false)}>
                      <FormattedMessage id="account-menu-my-account" defaultMessage="My Account" />
                    </Link>
                    <button className="wings-app-nav-profile-menu-item" role="menuitem" onClick={handleLogout}>
                      <FormattedMessage id="account-menu-logout" defaultMessage="Logout" />
                    </button>
                  </div>
                )}
              </div>
            </OutsideClickHandler>
          </div>
        </div>
      </div>

      {/* Get Started Flyout */}
      {showFlyout && (
        <GetStartedFlyout
          profile={profile}
          show={showFlyout}
          setShow={setShowFlyout}
          onStepChange={handleWalkThroughStepChange}
          stepsData={walkThroughStepValues}
        />
      )}
    </>
  )
}

export default WingsAppNav
